import { Container, Text, TextStyle } from 'pixi.js';
import { HUD_H, SCENE_H, SCENE_W, type GameConfig, type TeamId } from '@rekrut/shared';
import type { Engine } from '../sim/engine.js';
import type { MatchState } from '../sim/state.js';

/**
 * Верхняя панель. Проверка «правила трёх секунд»: зритель, зашедший на
 * случайной секунде, должен сразу понять, кто с кем, сколько живых у каждой
 * стороны и что нужно написать, чтобы попасть в бой.
 */
export class Hud {
  readonly view = new Container();

  private readonly labelA: Text;
  private readonly labelB: Text;
  private readonly countA: Text;
  private readonly countB: Text;
  private readonly timer: Text;
  private readonly phase: Text;
  private readonly hint: Text;
  private readonly score: Text;
  /** Буквы команд: по ним зритель понимает, что писать в чат. */
  private readonly keyA: Text;
  private readonly keyB: Text;
  private readonly hintA: Text;
  private readonly hintB: Text;
  private readonly hintMid: Text;
  private readonly hintOr: Text;
  private readonly hintTail: Text;
  private last = new Map<string, string>();
  /**
   * Ширина панели. В альбомном режиме кадр становится шире логической сцены,
   * и счёт с названиями команд должен разъехаться по краям, а не жаться
   * колонкой посередине.
   */
  private w = SCENE_W;

  constructor(private readonly engine: Engine) {
    const mk = (size: number, weight: string, color: number): Text =>
      new Text({
        text: '',
        style: new TextStyle({
          fontFamily: 'system-ui, "Noto Color Emoji", sans-serif',
          fontSize: size,
          fontWeight: weight as never,
          fill: color,
          stroke: { color: 0x05070c, width: Math.max(2, size * 0.14) },
        }),
      });

    this.labelA = mk(34, '800', 0xffffff);
    this.labelB = mk(34, '800', 0xffffff);
    this.countA = mk(48, '900', 0xffffff);
    this.countB = mk(48, '900', 0xffffff);
    this.timer = mk(34, '800', 0xffffff);
    this.phase = mk(19, '700', 0x8fa0bb);
    this.hint = mk(26, '800', 0xdfe8f7);
    // Счёт по раундам — единственное, что переходит из матча в матч.
    this.score = mk(30, '900', 0xffd25e);

    // Крупная буква рядом с названием: связка «A — красные, B — синие»
    // должна читаться мгновенно, иначе зритель пишет не за ту сторону.
    this.keyA = mk(42, '900', 0xffffff);
    this.keyB = mk(42, '900', 0xffffff);
    this.hintA = mk(30, '900', 0xffffff);
    this.hintB = mk(30, '900', 0xffffff);
    this.hintMid = mk(26, '800', 0xdfe8f7);
    this.hintOr = mk(26, '800', 0xdfe8f7);
    this.hintTail = mk(26, '800', 0xdfe8f7);

    this.keyB.anchor.set(1, 0);
    this.labelB.anchor.set(1, 0);
    this.countB.anchor.set(1, 0);

    this.keyA.text = 'A';
    this.keyB.text = 'B';

    this.score.anchor.set(0.5, 0);
    this.timer.anchor.set(0.5, 0);
    this.phase.anchor.set(0.5, 0);
    // Подсказка собрана из кусков, чтобы буквы были в цветах своих команд.
    this.hint.anchor.set(0.5, 0);
    for (const t of [this.hintA, this.hintB, this.hintMid, this.hintOr, this.hintTail]) {
      t.anchor.set(0.5, 0);
    }
    this.hint.visible = false;
    this.layout();

    this.view.addChild(
      this.labelA,
      this.labelB,
      this.countA,
      this.countB,
      this.score,
      this.timer,
      this.phase,
      this.keyA,
      this.keyB,
      this.hint,
      this.hintA,
      this.hintMid,
      this.hintOr,
      this.hintB,
      this.hintTail,
    );
  }

  /** Альбомный режим: панель растягивается на всю ширину кадра. */
  setLandscape(on: boolean): void {
    this.w = on ? SCENE_H : SCENE_W;
    this.layout();
  }

  private layout(): void {
    const w = this.w;
    this.keyA.position.set(34, 22);
    this.labelA.position.set(90, 14);
    this.countA.position.set(90, 50);
    this.keyB.position.set(w - 34, 22);
    this.labelB.position.set(w - 90, 14);
    this.countB.position.set(w - 90, 50);
    this.score.position.set(w / 2, 12);
    this.timer.position.set(w / 2, 52);
    this.phase.position.set(w / 2, 92);
    this.hint.position.set(w / 2, HUD_H - 44);
  }

  update(s: MatchState, cfg: GameConfig): void {
    for (const team of ['A', 'B'] as TeamId[]) {
      const label = team === 'A' ? this.labelA : this.labelB;
      const count = team === 'A' ? this.countA : this.countB;
      const alive = this.engine.countAlive(team);
      const queued = s.teams[team].queue.length;
      this.set(label, `l${team}`, s.teams[team].label);
      this.set(count, `c${team}`, queued > 0 ? `${alive}  +${queued}` : `${alive}`);
      label.style.fill = s.teams[team].color;
      count.style.fill = s.teams[team].color;
    }

    this.set(this.score, 'sc', `${s.rounds.A} : ${s.rounds.B}`);

    const t = Math.max(0, s.phase === 'lobby' ? (s.phaseEndsAt - s.now) / 1000 : (s.now - s.startedAt) / 1000);
    const m = Math.floor(t / 60);
    this.set(this.timer, 't', `${m}:${String(Math.floor(t % 60)).padStart(2, '0')}`);

    // Осада — главное состояние матча, оно должно быть подписано словами.
    const siegeA = !s.thrones.A.shielded;
    const siegeB = !s.thrones.B.shielded;
    const phaseText = siegeA
      ? `ТРОН ${s.teams.A.label} ПОД УДАРОМ`
      : siegeB
        ? `ТРОН ${s.teams.B.label} ПОД УДАРОМ`
        : PHASE_TEXT[s.phase];
    this.set(this.phase, 'p', phaseText);
    this.phase.style.fill = siegeA || siegeB ? 0xff5c5c : 0x8fa0bb;

    // «ПИШИ A ИЛИ B В ЧАТ» — каждая буква цветом своей стороны.
    this.keyA.style.fill = s.teams.A.color;
    this.keyB.style.fill = s.teams.B.color;
    this.hintA.style.fill = s.teams.A.color;
    this.hintB.style.fill = s.teams.B.color;
    this.set(this.hintMid, 'hm', 'ПИШИ');
    this.set(this.hintA, 'ha', 'A');
    this.set(this.hintB, 'hb', 'B');
    this.set(this.hintTail, 'ht', 'В ЧАТ');

    const y = HUD_H - 46;
    const gap = 14;
    const w =
      this.hintMid.width + this.hintA.width + this.hintB.width + this.hintTail.width + gap * 4 + 44;
    let x = this.w / 2 - w / 2;
    const place = (t: Text): void => {
      t.position.set(x + t.width / 2, y);
      x += t.width + gap;
    };
    place(this.hintMid);
    place(this.hintA);
    this.set(this.hintOr, 'ho', 'ИЛИ');
    place(this.hintOr);
    place(this.hintB);
    place(this.hintTail);

    const pulse = 0.6 + Math.sin(s.now / 420) * 0.25;
    for (const t of [this.hintMid, this.hintTail, this.hintOr]) t.alpha = pulse;
    for (const t of [this.hintA, this.hintB]) t.alpha = 1;
    void cfg;
  }

  private set(node: Text, key: string, value: string): void {
    if (this.last.get(key) === value) return;
    this.last.set(key, value);
    node.text = value;
  }
}

const PHASE_TEXT: Record<string, string> = {
  lobby: 'СБОР АРМИЙ',
  battle: 'ШТУРМ',
  finale: 'ФИНАЛ',
  results: 'ИТОГИ',
};
